import React, { useState, useEffect } from "react";
import Base from "../admin_components/Admin_Base";
import "../EmployeeSection/Employeelist.css";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import { Button, Form, Badge } from "react-bootstrap";
import * as XLSX from "xlsx";
import { Breadcrumbs } from "@mui/material";
import Link from "@mui/material/Link";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import authAxios from "../Auth/useAxios";
import Swal from "sweetalert2";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ADMIN_API_BASE_URL } from "../Auth/Api";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";

const Employeelist = () => {
  const [employeeList, setList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterDesignation, setFilterDesignation] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const itemsPerPage = 33;

  useEffect(() => {
    fetchEmployeeList();
  }, []);

  const fetchEmployeeList = async () => {
    try {
      const response = await authAxios.get(
        `${ADMIN_API_BASE_URL}/main/admin/list-employee`
      );
      setList(response.data);
    } catch (error) {
      console.error("Error fetching employees:", error);
      setError("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = employeeList.filter(
    (emp) =>
      (filterStatus === "All" || emp.status === filterStatus) &&
      (filterDesignation === "All" || emp.designation === filterDesignation)
  );

  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const handleDownloadExcel = () => {
    const filteredData = filteredEmployees.map(
      ({ employeeReferenceId, fullName, designation, status, mobile, email }) => ({
        "Employee ID": employeeReferenceId,
        "Employee Name": fullName,
        Designation: designation,
        Status: status,
        Mobile: mobile,
        Email: email,
      })
    );

    if (filteredData.length === 0) {
      Swal.fire(
        "No data to export!",
        "Please apply a filter that includes results.",
        "warning"
      );
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Filtered_Employees");
    XLSX.writeFile(wb, "Filtered_Employee_List.xlsx");
  };

  const handleView = (employeeId) => {
    navigate(`/admin/employee-view/${employeeId}`);
  };

  const handleEdit = (employeeId) => {
    navigate(`/admin/employee-edit/${employeeId}`);
  };

  const handleDelete = async (id, employeeName) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete employee: ${employeeName}. This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      confirmButtonColor: "#d33",
    });

    if (result.isConfirmed) {
      try {
        await authAxios.delete(
          `${ADMIN_API_BASE_URL}/main/admin/delete-employee/${id}`
        );
        setList(employeeList.filter((employee) => employee.id !== id));
        Swal.fire("Deleted!", "Employee has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting employee:", error);
        Swal.fire("Error!", "Failed to delete employee.", "error");
      }
    }
  };

  const handleStatusChange = async (id, newStatus, employeeName) => {
    try {
      const isActive = newStatus === "true";
      
      await authAxios.put(
        `${ADMIN_API_BASE_URL}/main/admin/update-status/${id}?isActive=${isActive}`
      );

      // Update local state
      setList(prevList =>
        prevList.map(employee =>
          employee.id === id 
            ? { ...employee, active: isActive, status: isActive ? "Active" : "Inactive" }
            : employee
        )
      );

      Swal.fire("Success!", `Status updated for ${employeeName}.`, "success");
    } catch (error) {
      console.error("Error updating status:", error);
      Swal.fire("Error!", "Failed to update status.", "error");
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <Badge bg="success">Active</Badge>
    ) : (
      <Badge bg="secondary">Inactive</Badge>
    );
  };

  return (
    <Base>
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "300px" }}>
          <ClipLoader color="darkslategrey" size={50} />
        </div>
      ) : error ? (
        <div className="alert alert-danger text-center">{error}</div>
      ) : (
        <>
          <div
            className="pt-3 mt-5 res_container"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: "20px",
            }}
          >
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
              className="admin_navigation"
            >
              <Link
                underline="hover"
                color="inherit"
                href="/admin/dashboard"
                sx={{ color: "darkslategrey", fontWeight: "bold" }}
              >
                Dashboard
              </Link>
              <Link
                underline="none"
                color="inherit"
                href="/admin/admin_employee_list"
                sx={{ color: "darkslategrey", fontWeight: "bold" }}
              >
                Employee List
              </Link>
            </Breadcrumbs>
          </div>

          <h2 className="text-center fw-bold main_heading">Employees List</h2>
          
          <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
            <div className="emp_filters" style={{ display: "flex", justifyContent: "space-between", width: "95%" }}>
              <Form.Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="filter-dropdown"
              >
                <option value="All">All Employees</option>
                <option value="Active">Active Employees</option>
                <option value="Inactive">Inactive Employees</option>
              </Form.Select>
              
              <Form.Select
                value={filterDesignation}
                onChange={(e) => setFilterDesignation(e.target.value)}
                className="filter-dropdown"
              >
                <option value="All">All Designations</option>
                {Array.from(new Set(employeeList.map((emp) => emp.designation))).map(
                  (designation, index) => (
                    <option key={index} value={designation}>
                      {designation}
                    </option>
                  )
                )}
              </Form.Select>
              
              <Button className="csv_listemp_button" onClick={handleDownloadExcel}>
                Download Excel
              </Button>
            </div>
          </div>

          <div className="table-container">
            <Table className="employee-table">
              <Thead>
                <Tr>
                  <Th>S.No</Th>
                  <Th>Employee ID</Th>
                  <Th>Employee Name</Th>
                  <Th>Designation</Th>
                  <Th>Mobile Number</Th>
             
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedEmployees.length > 0 ? (
                  paginatedEmployees.map((employee, index) => (
                    <Tr key={employee.id}>
                      <Td>{(currentPage - 1) * itemsPerPage + index + 1}</Td>
                      <Td>
                        <strong>{employee.employeeReferenceId}</strong>
                      </Td>
                      <Td>{employee.fullName}</Td>
                      <Td>{employee.designation}</Td>
                      <Td>{employee.mobile}</Td>
                     
                      <Td>
                        <div className="d-flex align-items-center gap-2">
                          {getStatusBadge(employee.active)}
                          <Form.Select
                            size="sm"
                            value={employee.active?.toString()}
                            onChange={(e) => handleStatusChange(employee.id, e.target.value, employee.fullName)}
                            style={{ width: "120px" }}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </Form.Select>
                        </div>
                      </Td>
                      <Td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="info"
                            size="sm"
                            onClick={() => handleView(employee.id)}
                            title="View Employee"
                          >
                            <VisibilityIcon fontSize="small" />
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleEdit(employee.id)}
                            title="Edit Employee"
                          >
                            <EditIcon fontSize="small" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(employee.id, employee.fullName)}
                            title="Delete Employee"
                          >
                            <DeleteIcon fontSize="small" />
                          </Button>
                        </div>
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan="8" className="text-center py-4">
                      No employees found matching the selected filters.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="pagination-controls">
              <Button
                variant="outline-primary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline-primary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </Base>
  );
};

export default Employeelist;